import {
  Box,
  Collapse,
  CircularProgress,
  ListItem,
  ListItemIcon,
  ListItemText,
  Switch,
  Typography,
} from "@mui/material";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { IconButton } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import useAuthentication from "../../hooks/useAuthentication";
import { updateUserProfile } from "../../utility/httpActions/userActions";
import { frontendRoutes } from "../../utility/frontendRoutes";
import { useFeatureFlag } from "../../hooks/useFeatureFlags";
import {
  NotificationPreferencesSection,
  SWITCH_SX,
} from "./NotificationPreferencesSection";

const NotificationSettings = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentUser, setCurrentUser } = useAuthentication();
  const whatsappEnabled = useFeatureFlag("whatsapp");
  const smsNotificationsEnabled = useFeatureFlag("sms-notifications");
  const emailNotificationsEnabled = useFeatureFlag("email-notifications");

  const { mutate: updateProfile, isPending } = useMutation({
    mutationFn: updateUserProfile,
    onSuccess: (data) => {
      if (data?.user) setCurrentUser(data.user);
      queryClient.invalidateQueries({ queryKey: ["currentUser"] });
    },
  });

  if (!currentUser) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const notifPrefs = currentUser.notificationPreferences || {};

  const updateNotifEnabled = (value) => {
    updateProfile({ notificationPreferences: { enabled: value } });
  };
  const updateNotifEvent = (event, value) => {
    updateProfile({ notificationPreferences: { events: { [event]: value } } });
  };
  const updateNotifChannel = (channel, value) => {
    updateProfile({
      notificationPreferences: { channels: { [channel]: value } },
    });
  };

  return (
    <Box sx={{ maxWidth: 600, mx: "auto", mt: 2, mb: 6 }}>
      <Box sx={{ display: "flex", alignItems: "center", px: 2, mb: 2 }}>
        <IconButton
          onClick={() => navigate(frontendRoutes.ACCOUNT_SETTINGS)}
          sx={{ mr: 1 }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" fontWeight={700}>
          Notification Settings
        </Typography>
      </Box>

      <Box sx={{ bgcolor: "white", borderRadius: 2, overflow: "hidden" }}>
        <ListItem sx={{ py: 1.25 }}>
          <ListItemIcon sx={{ minWidth: 40 }}>
            <NotificationsOutlinedIcon
              sx={{ fontSize: 22, color: "#046287" }}
            />
          </ListItemIcon>
          <ListItemText
            primary="All notifications"
            secondary="Master on/off for all alerts"
            primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }}
            secondaryTypographyProps={{ fontSize: 12 }}
          />
          <Switch
            checked={notifPrefs.enabled !== false}
            onChange={(e) => updateNotifEnabled(e.target.checked)}
            disabled={isPending}
            sx={SWITCH_SX}
          />
        </ListItem>

        <Collapse in={notifPrefs.enabled !== false} timeout={250}>
          <NotificationPreferencesSection
            notifPrefs={notifPrefs}
            onUpdateEvent={updateNotifEvent}
            onUpdateChannel={updateNotifChannel}
            isPending={isPending}
            whatsappEnabled={whatsappEnabled}
            smsNotificationsEnabled={smsNotificationsEnabled}
            emailNotificationsEnabled={emailNotificationsEnabled}
            userEmail={currentUser.email}
            isEmailVerified={currentUser.isEmailVerified}
          />
        </Collapse>
      </Box>
    </Box>
  );
};

export default NotificationSettings;
